/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

import Loading from 'components/Loading/Loading';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import AddNewSiteForm from './AddNewSiteForm';
import AddNewSiteSuccess from './AddNewSiteSuccess';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';
import AddNewCompanySuccess from '../AddNewCompany/AddNewCompanySuccess';

type TypeProps = {|
  formData: Object,
  actionDrawerStore: TypeActionDrawerStore,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class AddNewSite extends React.Component<*> {
  @observable vModelData = this.props.model;

  /**
   * Our Default field list, of which is overridable by props
   * We use this to be
   * @type {{site: string, system: string, company: string, account: string, building: string, room: string}}
   */
  defaultFieldList: Object = {
    account: 'chooseAccount.tradingAccount',
    company: 'location.locationCompany',
    site: 'location.locationSite',
    country: 'location.countryId',
    building: 'location.locationBuilding',
    room: 'location.locationRoom',
    system: 'location.locationSystem',
  };

  /**
   * Our reset field list. We use this to know which fields we want to reset
   * @type {{site: string, system: string, building: string, room: string}}
   */
  resetFieldList: Array = ['building', 'room', 'system'];

  createForm = () => {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const {
      parentForm,
      fieldList = null,
      isAssetRequest = false,
      locationType = null,
    }: Object = this.props;

    if (fieldList) {
      this.defaultFieldList = fieldList;
    }

    const fields: Array<Object> = [
      {
        name: 'addSiteForm',
        label: t('general:actions.new_site'),
        title: 'New Site',
        fields: [
          {
            name: 'tradingAccount',
            label: t('general:trading_account'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$(`${this.defaultFieldList.account}`).extra.selectID),
            value: parentForm.$(`${this.defaultFieldList.account}`).extra.selectID,
          },
          {
            name: 'company',
            label: t('general:company'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$(`${this.defaultFieldList.company}`).extra.selectID),
            value: parentForm.$(`${this.defaultFieldList.company}`).extra.selectID,
          },
          {
            name: 'companyName',
            label: t('general:company'),
            rules: 'required|string',
            disabled: true,
            value: parentForm.$(`${this.defaultFieldList.company}`).value,
          },
          {
            name: 'siteName',
            label: t('general:locations.labels.site'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'streetNo',
            label: t('general:labels.street_number'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'townCity',
            label: t('general:town_city'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'stateCountryProvince',
            label: t('general:state_county_province'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'zipCode',
            label: t('general:zip_postcode'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'country',
            label: t('general:country'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'contactPhoneNo',
            label: t('general:labels.contact_phone_number'),
            rules: 'required|telephone',
            value: '',
          },
          {
            name: 'contactFaxNo',
            label: t('general:labels.contact_fax_number'),
            rules: 'telephone',
            value: '',
          },
          {
            name: 'locationContact',
            label: t('general:locations.labels.contact'),
            rules: 'string',
            value: '',
          },
          {
            name: 'buildingtransport',
            label: t('general:locations.labels.building_transport'),
            rules: 'string',
            value: '',
          },
          {
            name: 'roomLab',
            label: t('general:locations.labels.lab_room'),
            rules: 'string',
            value: '',
          },
          {
            name: 'systemWorkspace',
            label: t('general:locations.labels.system_workspace'),
            rules: 'string',
            value: '',
          },
          {
            name: 'systemWorkspaceIsOwned',
            label: t('general:locations.messages.system_workspace_is_owned'),
            rules: 'string',
            value: '',
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const {
          company,
          siteName,
          streetNo,
          townCity,
          stateCountryProvince,
          zipCode,
          country,
          contactPhoneNo,
          contactFaxNo,
          locationContact,
        }: Object = data.values().addSiteForm;

        actionDrawerStore.userAddLocation(
          {
            company_id: company,
            name: siteName,
            country_id: country,
            contact: locationContact,
            contact_phone: contactPhoneNo,
            contact_fax: contactFaxNo,
            address: {
              postcode: zipCode,
              address_1: streetNo,
              address_2: townCity,
              address_4: stateCountryProvince,
            },
          },
          isAssetRequest,
          locationType
        );
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: t('general:actions.new_site'),
      }
    );
  };

  /**
   * Resets field values
   */
  resetFieldObjects() {
    const { parentForm } = this.props;

    for (const fieldName in this.defaultFieldList) {
      if (this.resetFieldList[fieldName]) {
        const field: Object = parentForm.$(`${fieldName}`);
        const fieldExtra: Object = field.extra;

        field.set('value', '');
        field.set('extra', { ...fieldExtra, selectID: '' });
        field.set('extra', { ...fieldExtra, parentId: '' });
      }
    }
  }

  /**
   * Resets all the data that was retrieved
   * in the api call
   */
  resetFieldData() {
    const { resetModels = [], resetFields = [] }: TypeProps = this.props;

    for (let i: * = 0; i < resetModels.length; i++) {
      resetModels[i].clear();
      resetModels[i].loadData();
    }

    for (let i: * = 0; i < resetFields.length; i++) {
      resetFields[i].extra.selectID = '';
    }
  }

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const parentForm: Object = this.props.parentForm;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.create_new_site')} />
          <div className={styles['action-drawer-content']}>
            {actionDrawerStore.errorResponse.message}
          </div>
          <div className={styles['action-drawer-actions']}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => actionDrawerStore.closeActionDrawer()}
            >
              {t('general:actions.close')}
            </Button>
          </div>
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <AddNewSiteSuccess
          parentForm={parentForm}
          model={this.props.model}
          fields={this.defaultFieldList}
          resetModels={this.props.resetModels}
          resetFields={this.props.resetFields}
          onMountHook={() => {
            this.resetFieldObjects();
            this.resetFieldData();
          }}
        />
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.create_new_site')} />
        <AddNewSiteForm
          form={this.createForm()}
          fieldList={this.defaultFieldList}
          parentForm={this.props.parentForm}
          accountsModel={this.props.accountsModel}
          companyModel={this.props.companyModel}
        />
      </div>
    );
  }
}
